import { AnchorId } from '../config/anchor.registry';
import { resolveClickable } from '../services/resolve-clickable';

/**
 * Geteilte DOM-Helfer der neun Aufgaben-Hilfe-Touren.
 *
 * Warum zentral: die Touren teilen sich dieselben drei Muster (Anker-Existenz,
 * eingeklappte `xds-section`, Toggle-Panels). Neunmal kopiert wuerde jede
 * spaetere Korrektur achtmal vergessen.
 *
 * Alle Funktionen sind bewusst defensiv: sie duerfen in einem Zustand laufen,
 * in dem ihr Zielelement gar nicht existiert, und tun dann nichts. Ein
 * `onBeforeShow` darf die Tour nie zum Absturz bringen.
 */

/** Host-Element eines Ankers oder `null`. Kein Throw, auch ohne `document`. */
export function anchorEl(anchor: AnchorId): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(`[onboardingAnchor="${anchor}"]`);
}

/**
 * DOM-Existenz-Pruefung fuer `skipIf`. Die `OnboardingAnchorDirective`
 * spiegelt den Wert als gleichnamiges Attribut aufs Host-Element, deshalb ist
 * der Attribut-Selektor derselbe String wie der Enum-Wert.
 *
 * ⚠️ Taugt NICHT fuer eingeklappte `xds-section`: deren Body wird per
 * `[hidden]` versteckt, das Host-Element bleibt im DOM. Dafuer
 * {@link ensureSectionExpanded} benutzen, sonst haengt der Step 8 s im
 * Sichtbarkeits-Guard, statt sauber zu ueberspringen.
 */
export function anchorMissing(anchor: AnchorId): boolean {
  return typeof document !== 'undefined' && !anchorEl(anchor);
}

/**
 * Startvorbereitung (`launchContext.prepare`) der Auftrags-Touren: schaltet `/tasks/overview`
 * auf die Ansicht „Aufträge“ (letztes Segment des Umschalters). Die Seite übernimmt
 * `?view=projects` nur beim ersten Aufbau, eine Navigation auf derselben Seite blieb in der
 * Liste stehen. Bei offenem Dialog (Start über das „?“ darin) tut sie nichts.
 */
export async function ensureProjectsView(): Promise<void> {
  if (typeof document === 'undefined' || document.querySelector('mat-dialog-container')) return;
  const host = anchorEl(AnchorId.TasksViewSwitch);
  if (!host || !host.hasAttribute('data-projects-view')) return;
  const segments = host.querySelectorAll<HTMLElement>('[role="radio"]');
  const projects = segments[segments.length - 1];
  if (!projects || projects.getAttribute('aria-checked') === 'true') return;
  projects.click();
  await settle(600);
}

/** Ein Frame + kurzer Puffer, damit Angular die Aenderung gerendert hat. */
function settle(ms = 220): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Klappt eine `xds-section` auf, falls sie zu ist.
 *
 * `xds-section` ist KEIN Material-Accordion — `panelIndex` greift dort nicht.
 * Die Komponente rendert den Body als `<div class="xds-section__body"
 * [hidden]="collapsible && !expanded">` und traegt den Zustand als
 * `aria-expanded` auf dem INNEREN `<button>` von `.xds-section__chevron`
 * (seit F16b, 10.10.2026, ueber `xds-icon-button[ariaExpanded]` — vorher stand
 * das Attribut ARIA-widrig am Host). Wir klicken deshalb genau diesen Knopf —
 * und nur dann, wenn er wirklich zugeklappt meldet (idempotent, mehrfacher
 * Aufruf schadet nicht).
 */
export async function ensureSectionExpanded(anchor: AnchorId): Promise<void> {
  const chevron = anchorEl(anchor)?.querySelector<HTMLElement>(
    '.xds-section__chevron button'
  );
  if (!chevron || chevron.getAttribute('aria-expanded') === 'true') return;
  chevron.click();
  await settle();
}

/**
 * Zustand der EINEN Primaeraktion im Aufgaben-Detail-Dialog.
 *
 * Der Server bestimmt ueber `AllowedActions`, welcher der sieben Zweige aus
 * `primary-action.ts` gilt; die Fussleiste spiegelt ihn als
 * `data-primary-kind`. Die Detail-Tour waehlt daran ihre Textvariante, statt
 * den Knopftext zu raten (der ist uebersetzt und aendert sich mit der Sprache).
 *
 * Liefert `null`, wenn die Fussleiste gar nicht steht — dann gibt es keine
 * erlaubte Aktion, und der zugehoerige Schritt wird ohnehin uebersprungen.
 */
export function detailPrimaryKind(): string | null {
  return (
    anchorEl(AnchorId.TaskDetailFooter)?.getAttribute('data-primary-kind') ??
    null
  );
}

/**
 * Art der Zustandskarte im Aufgaben-Detail (`data-state-kind` innen, z. B.
 * `reported`, `pool`, `waiting`, `done`) oder `null`, wenn keine Karte steht.
 * Die Detail-Tour waehlt daran Station bzw. Textvariante — wie bei
 * {@link detailPrimaryKind} nie am uebersetzten Text.
 */
export function detailStateKind(): string | null {
  const card = anchorEl(AnchorId.TaskDetailStateCard);
  if (!card) return null;
  const own = card.getAttribute('data-state-kind');
  if (own) return own;
  return (
    card.querySelector<HTMLElement>('[data-state-kind]')?.getAttribute('data-state-kind') ??
    null
  );
}

/**
 * Ist das Panel offen?
 *
 * Grundregel: die **Existenz des Panels** beweist den Zustand — nicht ein
 * vermutetes `aria-pressed` am Knopf (dokumentierte Stolperfalle, siehe
 * `CONTEXTUAL_HELP_PATTERN.md`, Abschnitt „Verhalten & Stolperfallen").
 *
 * Ausnahme: Panels, die IMMER im DOM stehen und ihren Zustand selbst tragen,
 * melden ihn als `data-open="true|false"` am Anker-Element. Die Sichten-Leiste
 * der Uebersicht ist so eins — sie steht auch zugeklappt im DOM, und die reine
 * Existenzpruefung hielt sie deshalb fuer offen und klappte sie nie auf
 * (Befund D-1, 06.10.2026).
 */
export function panelIsOpen(panelAnchor: AnchorId): boolean {
  const panel = anchorEl(panelAnchor);
  if (!panel) return false;
  const flag = panel.getAttribute('data-open');
  return flag === null ? true : flag === 'true';
}

/**
 * Bringt ein Toggle-Panel in den gewuenschten Zustand (idempotent).
 *
 * Geklickt wird das **Bedienelement im Anker** ({@link resolveClickable}), nicht
 * der Anker selbst: Sitzt der Anker auf dem Host von `xds-icon-button`, erreicht
 * ein `click()` am Host den `(click)`-Handler des inneren `<button>` nie. Genau
 * deshalb oeffnete `ov-quickadd` die Schnellerfassung nicht (Befund D-2).
 *
 * @param toggleAnchor Knopf, der das Panel oeffnet/schliesst
 * @param panelAnchor  Panel, dessen Zustand {@link panelIsOpen} liest
 * @param open         Zielzustand
 */
export async function ensurePanel(
  toggleAnchor: AnchorId,
  panelAnchor: AnchorId,
  open: boolean
): Promise<void> {
  if (panelIsOpen(panelAnchor) === open) return;
  const toggle = resolveClickable(anchorEl(toggleAnchor));
  if (!toggle) return;
  toggle.click();
  await settle();
}

/**
 * Die Anker aus `candidates`, die gerade im DOM stehen — in der Reihenfolge der
 * Liste, hoechstens `max` Stueck.
 *
 * Fuer `additionalSpotlightAnchors`, deren Ziel vom Bestand abhaengt (Gruppen
 * mit und ohne Auftraege): ein fehlender Zusatz-Anker kostet den Schritt sonst
 * zwei Sekunden Wartezeit und zeichnet trotzdem nichts.
 */
export function presentAnchors(
  candidates: readonly AnchorId[],
  max = candidates.length
): AnchorId[] {
  return candidates.filter((a) => !anchorMissing(a)).slice(0, max);
}
